// The typed report table the restaurant and platform scopes share: the
// server's one report shape (`Report` — typed columns, rows as objects, a
// totals row, an optional summary) rendered column by column from its
// `columnType`, the period grammar (a preset or an explicit from/to), and
// the CSV/XLSX renderings fetched through `format=` and saved as a file.

import { Download } from 'lucide-react';
import { api, money, type Report, type ReportColumn } from '../../lib/api';

// ── period grammar ───────────────────────────────────────────────────

export type Period = { preset: string | null; from: string; to: string };

export const PRESETS: { key: string; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'last_7_days', label: 'Last 7 days' },
  { key: 'this_week', label: 'This week' },
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'last_30_days', label: 'Last 30 days' },
];

/** Explicit dates win on the server; send one or the other, never both. */
export function periodParams(p: Period): Record<string, string> {
  if (p.preset === null && p.from && p.to) return { from: p.from, to: p.to };
  return { preset: p.preset ?? 'today' };
}

export function PeriodPicker(props: { value: Period; onChange: (p: Period) => void; presets?: string[] }) {
  const { value, onChange } = props;
  const presets = props.presets ? PRESETS.filter((p) => props.presets!.includes(p.key)) : PRESETS;
  const custom = value.preset === null;
  return (
    <div className="filters" data-section="period">
      {presets.map((p) => (
        <button key={p.key} className={'filter' + (value.preset === p.key ? ' on' : '')}
          onClick={() => onChange({ preset: p.key, from: '', to: '' })}>
          {p.label}
        </button>
      ))}
      <button className={'filter' + (custom ? ' on' : '')} onClick={() => onChange({ ...value, preset: null })}>
        Custom
      </button>
      {custom && (
        <>
          <input className="input" type="date" aria-label="From" value={value.from} style={{ width: 150 }}
            onChange={(e) => onChange({ ...value, from: e.target.value })} />
          <span style={{ color: 'var(--ink-3)' }}>→</span>
          <input className="input" type="date" aria-label="To" value={value.to} style={{ width: 150 }}
            onChange={(e) => onChange({ ...value, to: e.target.value })} />
        </>
      )}
    </div>
  );
}

// ── cells ────────────────────────────────────────────────────────────

type Value = string | number | null | undefined;

const NUMERIC = new Set(['integer', 'money', 'percent', 'minutes', 'decimal']);

export function formatCell(v: Value, columnType: string): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v !== 'number') {
    if (columnType === 'date_time') return new Date(v).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
    return String(v);
  }
  switch (columnType) {
    case 'money': return money(v);
    case 'percent': return `${(v / 100).toLocaleString('en-AU', { maximumFractionDigits: 2 })}%`;
    case 'minutes': return `${v.toLocaleString('en-AU')} min`;
    case 'decimal': return v.toLocaleString('en-AU', { maximumFractionDigits: 2 });
    default: return v.toLocaleString('en-AU');
  }
}

/** The summary's figures carry no columns of their own; the key says money. */
const summaryType = (key: string): string =>
  /liability|_in_cents$|revenue|spend|amount/.test(key) ? 'money' : 'integer';

const humanize = (key: string): string => {
  const s = key.replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
};

// ── the table ────────────────────────────────────────────────────────

/** One report: the summary as stats, the table with its totals row. */
export function ReportTable(props: { report: Report; hide?: string[]; empty?: string }) {
  const { report, hide = [] } = props;
  const cols: ReportColumn[] = report.columns.filter((c) => !hide.includes(c.key));
  const totals = report.totals;
  return (
    <div data-section="report" data-report={report.report} data-range={`${report.range.from}..${report.range.to}`}>
      {report.summary && Object.keys(report.summary).length > 0 && (
        <div className="stats" style={{ marginBottom: 18 }} data-section="summary">
          {Object.entries(report.summary).map(([k, v]) => (
            <div className="stat" key={k}>
              <div className="overline">{humanize(k)}</div>
              <div className="v money" data-summary={k}>{formatCell(v, summaryType(k))}</div>
              <div className="d">&nbsp;</div>
            </div>
          ))}
        </div>
      )}
      <div className="panel" style={{ overflowX: 'auto' }}>
        {report.rows.length === 0 && <div className="loading">{props.empty ?? 'Nothing in this period.'}</div>}
        {report.rows.length > 0 && (
          <table className="tbl">
            <thead>
              <tr>
                {cols.map((c) => (
                  <th key={c.key} className={NUMERIC.has(c.columnType) ? 'num' : undefined}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody style={{ cursor: 'default' }}>
              {report.rows.map((row, i) => (
                <tr key={i} data-row={String(row[cols[0]?.key] ?? i)} style={{ cursor: 'default' }}>
                  {cols.map((c) => (
                    <td key={c.key} data-col={c.key} className={NUMERIC.has(c.columnType) ? 'num money' : undefined}>
                      {formatCell(row[c.key], c.columnType)}
                    </td>
                  ))}
                </tr>
              ))}
              {totals && (
                <tr data-totals style={{ cursor: 'default', fontWeight: 800, borderTop: '2px solid var(--line)' }}>
                  {cols.map((c, i) => (
                    <td key={c.key} data-col={c.key} className={NUMERIC.has(c.columnType) ? 'num money' : undefined}>
                      {c.key in totals ? formatCell(totals[c.key], c.columnType) : i === 0 ? 'Total' : ''}
                    </td>
                  ))}
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── exports ──────────────────────────────────────────────────────────

/** Fetch a rendering (`format=csv|xlsx`) and hand it to the browser as a file. */
export async function downloadReport(path: string, params: Record<string, string>, format: 'csv' | 'xlsx', stem: string): Promise<void> {
  const blob = await api.get<Blob>(path, { ...params, format });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${stem}.${format}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function ExportButtons(props: { onExport: (format: 'csv' | 'xlsx') => void; disabled?: boolean }) {
  return (
    <span style={{ display: 'inline-flex', gap: 8 }}>
      <button className="btn btn-outline btn-sm" disabled={props.disabled} onClick={() => props.onExport('csv')}>
        <Download size={13} /> CSV
      </button>
      <button className="btn btn-outline btn-sm" disabled={props.disabled} onClick={() => props.onExport('xlsx')}>
        <Download size={13} /> XLSX
      </button>
    </span>
  );
}
